<template>
  <div>
    <!-- Figma "UQ flow - Option 1": left column of cards (stepper + step content, equipment form / images),
         right "Details" card (Claim ID, Consumer Name, Add Notes); Continue pill on the bottom-right edge, Submit Quote under the left column -->
    <div v-if="isShowQuote"
      class="w-full relative border-1 rounded-2xl mb-[40px] px-[10px] laptop:px-[20px] largeDesktop:px-[30px] pt-[35px] pb-[40px] largeDesktop:pb-[45px] border-gray-300">
      <!-- breadcrumb bar, same as the S&R evaluation pages (EvalShell): list | Quote Details | steps so far -->
      <div
        class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px] font-roboto">
        <template v-for="(crumb, index) in trail" :key="crumb.label">
          <span v-if="index > 0" class="text-[#CCCCCC]">|</span>
          <a v-if="crumb.to" :href="$router.resolve(crumb.to).href" class="no-underline text-[#8479E1]"
            @click="openCrumb($event, crumb.to)">{{ crumb.label }}</a>
          <button v-else-if="crumb.state" type="button" class="p-0 border-0 bg-transparent text-[#8479E1]"
            @click="setCommponent(crumb.state)">{{ crumb.label }}</button>
          <span v-else class="text-[#000000]">{{ crumb.label }}</span>
        </template>
      </div>
      <!-- partner replacement banner: centred above the cards, same as the UQ detail -->
      <div v-if="checkNoteAlert()" class="flex justify-center mb-[20px]">
        <AlertNote />
      </div>
      <div class="flex flex-col laptop:flex-row gap-[20px] largeDesktop:gap-[30px] laptop:items-start">
        <div class="flex-1 min-w-0 flex flex-col gap-[20px]">
          <div class="uqCard">
            <TabButtons />
            <div v-if="componentState == 'Notes'">
              <ImageGallery :is-before="1" />
            </div>
            <QuoteItems v-if="quoteItems" ref="refQuoteItems" :no_of_units="no_of_units" />
            <QuoteSummary v-if="componentState == 'QuoteSummary'" :no_of_units="no_of_units" />
          </div>

          <!-- Create Quote: QuoteItems teleports its Finalize Quote button here, under the card on the right -->
          <div v-if="quoteItems" id="uqFinalizeAction" class="flex justify-center tablet:justify-end empty:hidden" />
          <div v-if="componentState == 'QuoteSummary' && !isMaintenanceQuote" class="uqCard">
            <CauseOfFailure ref="refCauseOfFailure" />
          </div>
          <div v-if="componentState !== 'QuoteItems' && componentState !== 'QuoteSummary'" class="uqCard">
            <div v-if="quoteDetail != null && quoteDetail.trade.title == 'Hvac'">
              <HvacEquipmentInfo v-if="equipmentInfo" ref="HvacEquipmentInfo"
                @equipment-info-change="handleHvacEquipmentInfo" />
            </div>
            <div v-else>
              <EquipmentInfo v-if="equipmentInfo" ref="equipmentInfo" @equipment-info-change="handleEquipmentInfo" />
            </div>
          </div>
          <!-- images after evaluation and fixing -->
          <div v-if="componentState == 'QuoteSummary'" class="uqCard">
            <ImageGallery :is-before="0" />
          </div>
          <!-- Finalize Quote: Submit Quote under the cards column, on its right -->
          <div v-if="componentState == 'QuoteSummary'" class="flex justify-center tablet:justify-end">
            <QuoteSummaryTotal :validate-before-submit="validateQuoteSummary" :save-before-submit="saveCauseOfFailure" />
          </div>
        </div>

        <!-- right: Details (Figma: Claim ID + Consumer Name in red labels, then Add Notes) -->
        <aside
          class="uqCard w-full laptop:w-[320px] largeDesktop:w-[350px] flex-shrink-0 font-roboto">
          <div class="flex items-center gap-1 pb-[15px]">
            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
            <p class="m-0 pl-2 text-[18px] largeDesktop:text-[20px] font-semibold text-[#333333]">
              Details
            </p>
          </div>
          <div class="flex flex-col gap-[10px] pb-[15px]">
            <div v-if="isPartnerQuote && quoteDetail?.partner_info?.job_number">
              <span class="block text-[13px] text-[#EA8A8A]">Claim ID:</span>
              <p class="m-0 text-[14px] largeDesktop:text-[16px] text-[#333333] break-words">
                {{ quoteDetail.partner_info.job_number }}
              </p>
            </div>
            <div>
              <span class="block text-[13px] text-[#EA8A8A]">Consumer Name:</span>
              <p class="m-0 text-[14px] largeDesktop:text-[16px] text-[#333333] break-words">
                {{ $helper.isEmpty(quoteDetail?.consumer?.full_name) ? '-' : quoteDetail.consumer.full_name }}
              </p>
            </div>
          </div>
          <noteBox inner-class="min-h-[200px] max-h-[260px]" />
        </aside>
      </div>

      <!-- page action on the bottom edge: Continue (equipment step); Submit Quote sits under the cards column -->
      <div v-if="componentState == 'Notes'"
        class="w-full absolute right-0 -bottom-[20px] flex justify-center tablet:justify-end items-center px-[10px] laptop:px-[20px] largeDesktop:px-[30px]">
          <button v-if="quoteDetail != null && quoteDetail.trade.title == 'Hvac'" type="button" :disabled="!btnEnable"
            class="uqContinue group" @click="hvacEquipmentInfoClicked()">
            {{ $route.query.is_edit == 1 ? "Continue" : "Continue Editing" }}
            <img src="../../assets/images/arrow-icon.png" alt=""
              class="w-[20px] h-[20px] object-contain group-disabled:hidden mobile:block">
            <img src="../../assets/images/gray-arrow.png" alt=""
              class="w-[20px] h-[20px] object-contain group-disabled:block mobile:hidden">
          </button>
          <button v-else type="button" :disabled="txtSerialDisable" class="uqContinue group"
            @click="equipmentInfoClicked()">
            {{ $route.query.is_edit == 1 ? "Continue" : "Continue Editing" }}
            <img src="../../assets/images/arrow-icon.png" alt=""
              class="w-[20px] h-[20px] object-contain group-disabled:hidden mobile:block">
            <img src="../../assets/images/gray-arrow.png" alt=""
              class="w-[20px] h-[20px] object-contain group-disabled:block mobile:hidden">
          </button>
      </div>
    </div>
  </div>
  <div id="modalMaintenance" ref="modalMaintenance" class="modal fade" tabindex="-1" data-bs-backdrop="static"
    data-bs-keyboard="false" aria-hidden="true" aria-labelledby="exampleModalLabel" aria-modal="true" role="dialog">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content shareModal">
        <div class="modal-body mobile:!p-2.5 tablet:!p-3 !pb-5 font-roboto">
          <div class="relative">
            <!-- <button type="button"
              class="absolute top-3 right-4 w-[30px] h-[30px] z-50 focus:!outline-none focus:!shadow-none"
              data-bs-dismiss="modal" aria-label="Close" @click="closeWaiveTradeCallFee()">
              <img src="@/assets/images/modal-cross-icon.png">
            </button> -->
            <div class="relative rounded-lg overflow-hidden mobile:h-[70px] tablet:h-[80px]">
              <img src="../../assets/images/profile-bg.png" alt="Background" class="w-full h-auto object-cover">
              <div class="flex items-center gap-1 p-3 absolute top-0 left-0 w-full h-full">
                <h4 class="text-[20px] m-0 font-roboto font-medium text-black-100">
                  Units Included for Service
                </h4>
              </div>
            </div>
          </div>
          <div class="p-2.5 pb-0 maintenanceModal">
            <p class="text-[16px] font-normal text-[#666666] text-center py-[20px] mb-0">
              How many HVAC systems are included for service under this claim?
            </p>
            <div class="grid grid-cols-2 gap-[30px] my-4">
              <label class="system-option h-[130px]">
                <input v-model="number_of_units" type="radio" name="system" value="1" class="hidden">

                <p class="text-left text-[#987D9A] m-0 text-[12px] font-bold">
                  1 System
                </p>

                <div :class="[
                  'group border border-[#EFEFEF] rounded-[10px] p-[15px] bg-white text-center transition-all duration-200 ease-in-out cursor-pointer relative flex justify-center items-center',
                  number_of_units == 1 && 'selected'
                ]">
                  <div class="absolute right-2.5 -top-3 w-[24px] h-[24px]">
                    <img src="../../assets/images/enabled-checked.png"
                      class="w-full h-full object-contain hidden group-[.selected]:block">
                    <img src="../../assets/images/disabled-checked.png"
                      class="w-full h-full object-contain block group-[.selected]:hidden">
                  </div>

                  <img src="../../assets/images/system-1.png" class="w-[130px] h-[95px] object-contain">
                </div>
              </label>
              <!-- Option 2 -->
              <label class="system-option h-[130px]">
                <input v-model="number_of_units" type="radio" name="system" value="2" class="hidden">

                <p class="text-left text-[#987D9A] m-0 text-[12px] font-bold">
                  2 Systems
                </p>

                <div :class="[
                  'group border border-[#EFEFEF] rounded-[10px] p-[15px] bg-white text-center transition-all duration-200 ease-in-out cursor-pointer relative flex justify-center items-center',
                  number_of_units == 2 && 'selected'
                ]">
                  <div class="absolute right-2.5 -top-3 w-[24px] h-[24px]">
                    <img src="../../assets/images/enabled-checked.png"
                      class="w-full h-full object-contain hidden group-[.selected]:block">
                    <img src="../../assets/images/disabled-checked.png"
                      class="w-full h-full object-contain block group-[.selected]:hidden">
                  </div>

                  <img src="../../assets/images/system-2.png" class="w-[130px] h-[95px] object-contain">
                </div>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 !pb-[30px] !pt-0 !justify-center">
          <div class="flex justify-center gap-2.5">
            <button
              class="px-[32px] py-[8px] rounded-[5px] text-[14px] font-roboto transition duration-300 border-[#00B7C2] border-1 mobile:bg-[#00B7C2] laptop:bg-[#fff] mobile:text-[#fff] laptop:text-[#00B7C2] laptop:hover:bg-[#00B7C2] laptop:hover:text-[#fff]"
              @click="nextModalClicked()">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content maintenanceModal p-0">
        <div class="flex items-center gap-2 px-4 py-4">
          <span class="w-2 h-2 rounded-full bg-[#FE5000]" />
          <span class="w-10 h-2 rounded-full bg-[#FE5000]" />
          <h2 class="m-0 font-lato font-[700] text-[20px] text-[#092E87]">
            Units Included for Service
          </h2>
        </div>
        <div class="modal-body">
          <p class="mb-0 text-center">
            How many HVAC systems are included for service under this claim?
          </p>
          <div class="system-options my-4">
            <label class="system-option">
              <input v-model="number_of_units" type="radio" name="system" value="1" checked>
              <div class="card selected">
                <img src="../../assets/images/outdoor-1.png" alt="1 System">
              </div>
              <p>1 System</p>
            </label>
            <label class="system-option">
              <input v-model="number_of_units" type="radio" name="system" value="2">
              <div class="card">
                <img src="../../assets/images/outdoor-2.png" alt="2 System">
              </div>
              <p>2 Systems</p>
            </label>
          </div>
          <div class="flex justify-center pb-2">
            <button
              class="px-4 py-1.5 border-1 rounded-full text-[16px] font-lato transition duration-300 border-[#FE5000] mobile:text-white laptop:text-[#FE5000] mobile:bg-[#FE5000] laptop:bg-white laptop:hover:bg-[#FE5000] laptop:hover:text-[#fff]"
              @click="nextModalClicked()">
              Next
            </button>
          </div>
        </div>
      </div>
    </div> -->
  </div>

  <div v-if="isNotesOpen" id="notesModal" class="modal fade modal-md show bg-opacity-50 bg-[#000]" tabindex="-1"
    aria-labelledby="exampleModalLabel" aria-modal="true" role="dialog" style="display: block">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content !rounded-[20px]">
        <div class="modal-body p-4 pb-5">
          <div class="flex flex-col gap-4 w-full">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-[#FE5000]" />
                <span class="w-10 h-2 rounded-full bg-[#FE5000]" />
                <h2 class="m-0 font-lato font-[700] text-[20px] text-[#092E87]">
                  Notes
                </h2>
              </div>
              <button class="w-[40px] h-[40px] p-2 rounded-full bg-opacity-60 bg-[#EFEFEF]" type="button"
                data-bs-dismiss="modal" aria-label="Close" @click="closeModal">
                <img src="@/assets/images/modal-cross-icon.png">
              </button>
            </div>
            <noteBox />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { isSilentLogin } from "@/helpers/embedMode";
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";
import { Modal } from "bootstrap";
import { nextTick } from 'vue';
import EquipmentInfo from "@/components/quote/EquipmentInfo.vue";
import HvacEquipmentInfo from "@/components/quote/HvacEquipmentInfo.vue";
import QuoteItems from "@/components/quote/QuoteItems.vue";
import QuoteSummary from "@/components/quote/QuoteSummary.vue";
import CauseOfFailure from "@/components/quote/CauseOfFailure.vue";
import TabButtons from "@/components/tab-buttons/TabButtons.vue";
import ImageGallery from "@/components/image_gallery/ImageGallery.vue";
import AlertNote from "@/components/alert_note/AlertNote.vue";
import noteBox from "../../components/notes/noteBox.vue";
import QuoteSummaryTotal from "../../components/quote/QuoteSummaryTotal.vue";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

export default {
  components: {
    EquipmentInfo,
    HvacEquipmentInfo,
    QuoteItems,
    QuoteSummary,
    CauseOfFailure,
    TabButtons,
    ImageGallery,
    AlertNote,
    noteBox,
    QuoteSummaryTotal,
  },

  data() {
    return {
      crumbs: ["Quote Details", "Equipment Info"],
      isShowQuote: false,
      notes: true,
      equipmentInfo: true,
      gallery: true,
      quoteItems: false,
      quoteSummary: false,
      no_of_units: -1,
      is_show_multi_units: false,
      number_of_units: -1,
      isNumberOfUnitsDialogShow: false,
      isNotesOpen: false,
      isFirstTimeSet: false,
      btnEnable: false,
      txtSerialDisable: true,
    };
  },
  computed: {
    ...mapGetters(["quoteDetail", "componentState"]),

    // list (not for silent login / SSO: the router logs them out there) | Quote Details | the steps reached so far
    trail() {
      const id = this.$route.params.id;
      // the images step exists only for partner quotes; a normal quote starts at Create Quote
      const steps = [
        ...(this.isPartnerQuote ? [{ state: "Notes", label: "Equipment Information" }] : []),
        { state: "QuoteItems", label: "Create Quote" },
        { state: "QuoteSummary", label: "Finalize Quote" },
      ];
      const current = Math.max(0, steps.findIndex(s => s.state === this.componentState));
      return [
        ...(isSilentLogin() ? [] : [{ label: "Universal Quotes", to: "/work-order/universal-quotes" }]),
        { label: "Quote Details", to: `/quote/detail/${id}` },
        ...steps.slice(0, current).map(s => ({ label: s.label, state: s.state })),
        { label: steps[current].label },
      ];
    },
    isPartnerQuote() {
      return (this.quoteDetail?.partner_info?.id > 0 && this.quoteDetail?.partner_info?.status != '0') || false
    },

    isMaintenanceQuote() {
      return (this.quoteDetail?.service_type?.title ?? '').toLowerCase() == 'maintenance'
    },
  },

  watch: {
    quoteDetail: function (data) {
      if (data.status.technician.status == 0) {
        this.isShowQuote = true;
      } else {
        this.$router.push({ path: `/quote/detail/${this.quoteDetail.id}` });
      }

      if (!this.isFirstTimeSet) {
        var component = this.isPartnerQuote ? "Notes" : "QuoteItems"
        if ((this.$route.query.is_from_mobile ?? 0) != '1') {
          component = JSON.parse(localStorage.getItem(this.$route.params.id + "-quote")) ?? (this.isPartnerQuote ? "Notes" : "QuoteItems");
        }
        console.log("component  ", component);
        this.setCommponent(component);
        this.setCompoenentState(component)
        this.isFirstTimeSet = true
      }
      if (this.quoteItems && !this.isNumberOfUnitsDialogShow) {
        this.isNumberOfUnitsDialogShow = true
        //  this.no_of_units = this.quoteDetail.number_of_units
        this.number_of_units = this.quoteDetail.number_of_units
        this.is_show_multi_units = this.quoteDetail.partner_info.is_show_multi_units

        if (this.is_show_multi_units) {
          nextTick(() => {
            this.modal = new Modal(this.$refs.modalMaintenance);
            this.modal.show();
            disableScroll()
          })
        }
        else {
          this.no_of_units = this.number_of_units
          if (this.$refs.refQuoteItems) {
            this.$refs.refQuoteItems.setUnits(this.number_of_units)
          }
        }
      }
      else if (this.no_of_units != -1) {
        this.no_of_units = this.number_of_units
        if (this.$refs.refQuoteItems) {
          this.$refs.refQuoteItems.setUnits(this.number_of_units)
        }
      }
    },

    componentState: function (data) {
      this.setCompoenentState(data)
    },
  },

  mounted() {
    this.getQuoteDetail({
      manual_quote_id: this.$route.params.id,
    });


    this.$refs.modalMaintenance.addEventListener('shown.bs.modal', () => {
      disableScroll()
    });

    this.$refs.modalMaintenance.addEventListener('hidden.bs.modal', () => {
      enableScroll()
    })
  },

  methods: {
    ...mapActions(["setCommponent", "getQuoteDetail"]),
    handleHvacEquipmentInfo(value) {
      this.btnEnable = value;
    },
    handleEquipmentInfo(value) {
      this.txtSerialDisable = value;
    },
    validateQuoteSummary() {
      return this.$refs.refCauseOfFailure
        ? this.$refs.refCauseOfFailure.validateCauseOfFailure()
        : true;
    },

    saveCauseOfFailure() {
      return this.$refs.refCauseOfFailure
        ? this.$refs.refCauseOfFailure.submitCauseOfFailure()
        : true;
    },

    openNotesModal() {
      disableScroll()
      this.isNotesOpen = true;
    },
    closeModal() {
      enableScroll()
      this.isNotesOpen = false;
    },
    openCrumb(event, to) {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      goBackTo(this.$router, to);
    },
    resetCommponent() {
      this.gallery = false;
      this.notes = false;
      this.equipmentInfo = false;
      this.quoteItems = false;
      this.quoteSummary = false;
    },
    hvacEquipmentInfoClicked() {
      this.$refs.HvacEquipmentInfo.storeHvacEquipmentInfoClicked();
    },
    equipmentInfoClicked() {
      this.$refs.equipmentInfo.storeEquipmentInfoClicked();
    },
    nextModalClicked() {
      this.no_of_units = this.number_of_units
      if (this.$refs.refQuoteItems) {
        this.$refs.refQuoteItems.setUnits(this.number_of_units)
      }
      enableScroll()
      this.modal.hide();
    },

    checkNoteAlert() {
      if (this.quoteDetail &&
        this.quoteDetail.partner_info
        && this.quoteDetail.partner_info.show_replacement_opportunity_badge == '1'
        && this.quoteDetail.partner_info.status == '1'
        && this.quoteDetail.partner_info.company_name != 'Vivint'
        && this.quoteDetail.partner_info.replacement_opportunity_number?.length > 0) {
        return true
      } else {
        return false
      }
    },

    backButtonClicked() {
      switch (this.componentState) {
        case "QuoteItems":
          this.setCommponent("Notes");
          break;
        case "QuoteSummary":
          this.setCommponent("QuoteItems");
          break;
      }
    },

    crumbClicked(item) {
      if (item == "Equipment Info") {
        this.setCommponent("Notes");
      } else if (item == "Quote Items") {
        this.setCommponent("QuoteItems");
      } else if (item == "Quote Details") {
        this.$router.push({ path: `/quote/detail/${this.quoteDetail.id}` });
      }
    },

    setCompoenentState(data) {
      localStorage.setItem(
        this.$route.params.id + "-quote",
        JSON.stringify(data)
      );
      this.crumbs = [];
      this.crumbs.push("Quote Details");
      if (this.isPartnerQuote) {
        this.crumbs.push("Equipment Info");
      }
      console.log("component  1", data);
      this.resetCommponent();

      switch (data) {
        case "Notes":
          this.notes = true;
          this.equipmentInfo = true;
          this.gallery = true;
          break;
        case "QuoteItems":
          this.notes = true;

          this.crumbs.push("Quote Items");
          this.quoteItems = true;
          // if (this.quoteDetail)
          this.getQuoteDetail({
            is_hide: true,
            manual_quote_id: this.$route.params.id,
          });
          break;
        case "QuoteSummary":
          this.notes = true;
          this.quoteSummary = true;
          this.gallery = false
          this.crumbs.push("Quote Items");
          this.crumbs.push("Summary");
          break;
      }
      nextTick(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth" // ya "auto"
        });
      })
    }
  },
};
</script>

<style>
/* Figma UQ cards: white, 20px radius, soft shadow */
.uqCard {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px;
  border-radius: 20px;
  background: #FFFFFF;
  box-shadow: 0px 0px 20px 0px #00000026;
}
@media (min-width: 1024px) {
  .uqCard {
    padding: 20px;
  }
}
/* Continue pill: teal; grey while the form isn't complete (Figma Equipment Info - Step 1) */
.uqContinue {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 40px;
  padding: 0 20px;
  border-radius: 9999px;
  border: 1px solid #29C6CD;
  background: #29C6CD;
  color: #FFFFFF;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 500;
}
.uqContinue:disabled {
  border-color: #EFEFEF;
  background: #EFEFEF;
  color: #999999;
  cursor: not-allowed;
}

.custom-breadcrumb {
  background-color: #fff;
}

.custom-breadcrumb a {
  margin: 5px 0 0;
  font-weight: 600;
}

.custom-breadcrumb .breadcrumb-item a {
  cursor: pointer;
  color: #ff5b01;
  text-decoration: underline;
}

.custom-breadcrumb .breadcrumb-item:last-child a {
  color: #7e7e7e;
  cursor: default;
  text-decoration: none;
}

.breadcrumbNavigation {
  padding: 1rem 0;
  border-bottom: 1px solid #ccc;
}

.maintenanceModal .modal-header {
  background: #ff5b01;
  border: 0;
  color: #fff;
}

/* .maintenanceModal .system-option input[type="radio"]:checked+.card {
  border-color: #f44e00;
  box-shadow: none;
} */
</style>
